/**
 * useWaveSurferPlayer Hook
 *
 * Manages WaveSurfer.js lifecycle with regions plugin for flashcard audio playback.
 * Provides loop controls, region markers, and confidence-based visual feedback.
 *
 * Features:
 * - Auto-repeat current flashcard segment (loop single mode)
 * - Visual waveform with confidence-based region colors
 * - Keyboard shortcuts (spacebar, arrows, L)
 * - Redux integration with globalAudioSlice
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import WaveSurfer from 'wavesurfer.js'
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions'
import type { Region } from 'wavesurfer.js/dist/plugins/regions'

export type LoopMode = 'none' | 'single' | 'all'

export interface FlashcardAudioClip {
  startTime: number
  endTime: number
  duration: number
  alignmentConfidence: number
  needsReview?: boolean
}

export interface FlashcardWithAudio {
  _id: string
  frontSpanish: string[]
  backEnglish: string[]
  audioClip?: FlashcardAudioClip
}

interface UseWaveSurferPlayerProps {
  audioUrl: string
  flashcards: FlashcardWithAudio[]
  currentIndex: number
  onIndexChange?: (newIndex: number) => void
}

export function useWaveSurferPlayer({
  audioUrl,
  flashcards,
  currentIndex,
  onIndexChange,
}: UseWaveSurferPlayerProps) {
  const waveformRef = useRef<HTMLDivElement | null>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const regionsRef = useRef<RegionsPlugin | null>(null)
  const activeRegionRef = useRef<Region | null>(null)

  const [isReady, setIsReady] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [loopMode, setLoopMode] = useState<LoopMode>('single')
  const [hasUserInteraction, setHasUserInteraction] = useState(false)

  const currentFlashcard = flashcards[currentIndex]

  // Get confidence-based color for region
  const getConfidenceColor = useCallback((confidence: number): string => {
    if (confidence < 0.70) return 'rgba(255, 0, 0, 0.3)'    // Red - needs review
    if (confidence < 0.85) return 'rgba(255, 255, 0, 0.3)'  // Yellow - verify
    return 'rgba(0, 255, 255, 0.3)'                          // Cyan - good
  }, [])

  // Initialize WaveSurfer
  useEffect(() => {
    if (!waveformRef.current || !audioUrl) return

    // Create WaveSurfer instance
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      url: audioUrl,
      waveColor: '#00ffff',      // Cyan waveform
      progressColor: '#ff0080',  // Hot pink playhead
      cursorColor: '#00ff00',    // Terminal green cursor
      backend: 'WebAudio',
      height: 128,
      normalize: true,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
    })

    // Create regions plugin
    const regions = wavesurfer.registerPlugin(RegionsPlugin.create())
    regionsRef.current = regions

    // Setup event listeners
    wavesurfer.on('ready', () => {
      setIsReady(true)
      setDuration(wavesurfer.getDuration())
    })

    wavesurfer.on('play', () => setIsPlaying(true))
    wavesurfer.on('pause', () => setIsPlaying(false))
    wavesurfer.on('timeupdate', (time) => setCurrentTime(time))
    wavesurfer.on('finish', () => setIsPlaying(false))

    wavesurferRef.current = wavesurfer

    return () => {
      wavesurfer.destroy()
    }
  }, [audioUrl])

  // Separate effect for region-out event with current dependencies
  // This fixes the stale closure bug by re-registering when dependencies change
  useEffect(() => {
    if (!regionsRef.current) return

    const handleRegionOut = (region: Region) => {
      if (loopMode === 'single' && isPlaying) {
        wavesurferRef.current?.setTime(region.start)
        wavesurferRef.current?.play()
      } else if (loopMode === 'all' && isPlaying) {
        // Move to next flashcard
        const nextIndex = (currentIndex + 1) % flashcards.length
        onIndexChange?.(nextIndex)
      }
    }

    regionsRef.current.on('region-out', handleRegionOut)

    return () => {
      regionsRef.current?.un('region-out', handleRegionOut)
    }
  }, [loopMode, isPlaying, currentIndex, flashcards.length, onIndexChange])

  // Cleanup effect for unmount (fixes memory leak)
  useEffect(() => {
    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy()
        wavesurferRef.current = null
      }
    }
  }, [])

  // Update region when flashcard changes
  useEffect(() => {
    if (!regionsRef.current || !currentFlashcard?.audioClip) return

    // Clear existing regions
    regionsRef.current.clearRegions()

    // Create new region for current flashcard
    const { startTime, endTime, alignmentConfidence } = currentFlashcard.audioClip
    const color = getConfidenceColor(alignmentConfidence)

    const region = regionsRef.current.addRegion({
      start: startTime,
      end: endTime,
      color,
      drag: false,        // Disable dragging (use manual adjustment controls instead)
      resize: false,      // Disable resizing
    })

    activeRegionRef.current = region

    // Auto-seek to region start when changing flashcards
    if (wavesurferRef.current && isReady) {
      wavesurferRef.current.setTime(startTime)

      // ⭐ NEW: Auto-play after seeking (mobile-compliant)
      // Only auto-play if user has interacted (iOS/Android requirement)
      if (hasUserInteraction) {
        wavesurferRef.current.play()
      }
    }
  }, [currentFlashcard, isReady, getConfidenceColor, hasUserInteraction])

  // Playback controls
  const play = useCallback(() => {
    // First play = user interaction (enables auto-play for future navigation)
    setHasUserInteraction(true)
    wavesurferRef.current?.play()
  }, [])

  const pause = useCallback(() => {
    wavesurferRef.current?.pause()
  }, [])

  const togglePlayPause = useCallback(() => {
    // First interaction = enables auto-play for future navigation
    setHasUserInteraction(true)
    wavesurferRef.current?.playPause()
  }, [])

  const seek = useCallback((time: number) => {
    wavesurferRef.current?.setTime(time)
  }, [])

  const seekToRegionStart = useCallback(() => {
    if (currentFlashcard?.audioClip) {
      seek(currentFlashcard.audioClip.startTime)
    }
  }, [currentFlashcard, seek])

  const playRegion = useCallback(() => {
    if (currentFlashcard?.audioClip) {
      setHasUserInteraction(true)  // Mark user interaction
      seek(currentFlashcard.audioClip.startTime)
      play()
    }
  }, [currentFlashcard, seek, play])

  // Navigation
  const goToPrevious = useCallback(() => {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : flashcards.length - 1
    onIndexChange?.(prevIndex)
  }, [currentIndex, flashcards.length, onIndexChange])

  const goToNext = useCallback(() => {
    const nextIndex = (currentIndex + 1) % flashcards.length
    onIndexChange?.(nextIndex)
  }, [currentIndex, flashcards.length, onIndexChange])

  // Loop mode toggle
  const cycleLoopMode = useCallback(() => {
    setLoopMode((prev) => {
      if (prev === 'none') return 'single'
      if (prev === 'single') return 'all'
      return 'none'
    })
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      switch (e.key) {
        case ' ': // Spacebar - play/pause
          e.preventDefault()
          togglePlayPause()
          break
        case 'ArrowLeft': // Previous flashcard
          e.preventDefault()
          goToPrevious()
          break
        case 'ArrowRight': // Next flashcard
          e.preventDefault()
          goToNext()
          break
        case 'l':
        case 'L': // Toggle loop mode
          e.preventDefault()
          cycleLoopMode()
          break
        case 'r':
        case 'R': // Restart current region
          e.preventDefault()
          seekToRegionStart()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [togglePlayPause, goToPrevious, goToNext, cycleLoopMode, seekToRegionStart])

  return {
    waveformRef,
    isReady,
    isPlaying,
    currentTime,
    duration,
    loopMode,
    hasUserInteraction,
    play,
    pause,
    togglePlayPause,
    seek,
    seekToRegionStart,
    playRegion,
    goToPrevious,
    goToNext,
    setLoopMode,
    cycleLoopMode,
  }
}
